import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { toggleClass } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { DEFAULT_DATETIME_FORMAT, DEFAULT_DATE_FORMAT, NOW, UnixTimestamp, format, parse, set, toDate } from "@cervval.js/common/src/tools/timestamps.tools";
import { I18nCalendar } from "../calendar/calendar.i18n";
import { CjsDatePicker, DatePickerOptions } from "../datePicker/datePicker";
import { Picker, PickerOptions } from "../picker";
import { CjsTimePicker, TimePickerOptions } from "../timePicker/timePicker";

/** ##### TIME PICKER  ##### */
export interface AllDateTimePickerSubOptions {
    pickerOptions: Partial<DateTimePickerOptions>;
    datePickerOptions: Partial<DatePickerOptions>;
    timePickerOptions: Partial<TimePickerOptions>;
}

/** AllowTextInput allows text input to be modified
 * when using text input, you should set enableKeyboardInput: false in DatePickerOptions
 */
export type DateTimePickerOptions = PickerOptions & { allowTextInput?: boolean };


/** default parameters for the dateTimePicker */
export const defaultDateTimePickerOptions: DateTimePickerOptions = {
    disabled: false
};

/** CSS Classes  */
const PICKER_HIDDEN = "hidden";
const FA_CALENDAR = "fa-calendar-days";
const FA_CLOCK = "fa-clock";

/** Mode of DateTimePicker, show datePicker or timePicker */
type DateTimePickerMode = "date" | "time";

@CustomElement({
    name: "cjs-date-time-picker",
    style: require("./dateTimePicker.scss"),
    template: require("./dateTimePicker.html"),
    i18n: require("../calendar/calendar.i18n").I18n
})
export class CjsDateTimePicker extends Picker<UnixTimestamp, I18nCalendar> {

    @TypedWebComponentOptions(defaultDateTimePickerOptions)
    public override readonly options!: WebComponentOptions<DateTimePickerOptions>;

    /** Shortcut for datePicker options */
    public get datePickerOptions(): WebComponentOptions<DatePickerOptions> {
        return this.components.datePicker.options;
    }

    /** Shortcut for timePicker options */
    public get timePickerOptions(): WebComponentOptions<TimePickerOptions> {
        return this.components.timePicker.options;
    }

    @TypedHtmlElements({ ensureLoadedClasses: [CjsDatePicker, CjsTimePicker] })
    public readonly components!: {
        dateTimePickerContainer: HTMLElement,
        datePickerInput: HTMLInputElement,
        dateTimePickerValueContainer: HTMLElement,
        datePickerInputButtonIcon: HTMLElement,
        datePicker: CjsDatePicker,
        timePicker: CjsTimePicker
    };

    /** Date time select in picker, jsDate */
    protected override _value = NOW();

    /**
     * Current mode for dateTimePicker (date == datePicker / time == timePicker)
     */
    private _dateTimePickerMode: DateTimePickerMode = "date";


    /**
     * Getter for dateTimePickerMode
     * @see _dateTimePickerMode 
     */
    public get dateTimePickerMode(): DateTimePickerMode { return this._dateTimePickerMode; };

    constructor(pickersOptions?: Partial<AllDateTimePickerSubOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(pickersOptions?.pickerOptions);
        this.timePickerOptions.setWithoutRefresh(pickersOptions?.timePickerOptions);
        this.datePickerOptions.setWithoutRefresh(pickersOptions?.datePickerOptions);
    }

    /**
     * Setter [[selectedDateTime]] of the DateTimePicker
     */
    public override setValue(value: UnixTimestamp): void {
        this._value = value;
        const dateAndTimeValues = this.getTimeAndDateValue();
        this.components.datePicker.setValue(dateAndTimeValues.date);
        this.components.timePicker.setValue(dateAndTimeValues.time);
        this._updateInputTextValue();
    }

    /** Get time value in minutes and date from [[selectedDateTime]] */
    public getTimeAndDateValue(): { time: number, date: UnixTimestamp } {
        const tsDate = toDate(this._value);
        return { time: tsDate.minuteOfDay, date: tsDate.beginOf("day").uts };
    }

    /**
     * When component is initialized
     */
    public override init(): void {
        this._initListeners();
    }

    /** Refresh component display*/
    public override refresh(): void {
        super.refresh();
        this._refreshContainers();
        this._updateInputTextValue();

        /** If allow text input, remove disbaled if needed */
        if (this.options?.allowTextInput && this.components.datePickerInput.hasAttribute("disabled")) {
            this.components.datePickerInput.removeAttribute("disabled");
        }

        /** Set pickers display */
        toggleClass(this.components.timePicker, this._dateTimePickerMode === "date", PICKER_HIDDEN);
        toggleClass(this.components.datePicker, this._dateTimePickerMode !== "date", PICKER_HIDDEN);
    }

    /** Init containers listeners */
    private _initListeners(): void {
        this.components.datePicker.valueChangeEvent.on(this._onValueDatePickerSet.bind(this));
        this.components.timePicker.valueChangeEvent.on(this._onValueTimePickerSet.bind(this));
        this.components.dateTimePickerValueContainer.onclick = () => this.togglePicker();
        this.components.datePickerInput.onchange = () => this._updateDateAndTimeFromInput();
        this.components.datePickerInput.onclick = (ev) => { if (this.options?.allowTextInput) { ev.stopPropagation(); } };
    }

    /** Init containers and set pickers options  */
    private _refreshContainers(): void {
        this.components.datePicker.refresh();
        this.components.timePicker.refresh();

        this.setValue(this._value);
    }

    /** On inputWithButton button click swipe to timePicker from datePicker  */
    public togglePicker(): void {
        if (this.disabled) { return; }// Don't allow togglePicker callback if component is disabled

        if (this._dateTimePickerMode === "date") {
            // Show/Hide pickers
            this.components.datePicker.classList.add(PICKER_HIDDEN);
            this.components.timePicker.classList.remove(PICKER_HIDDEN);

            // Update button icon
            this.components.datePickerInputButtonIcon.classList.remove(FA_CLOCK);
            this.components.datePickerInputButtonIcon.classList.add(FA_CALENDAR);

            // Update timePicker option value
            this.components.timePicker.setValue(this.getTimeAndDateValue().time);

            // Update mode
            this._dateTimePickerMode = "time";
        } else if (this._dateTimePickerMode === "time") {

            // Show/Hide pickers
            this.components.timePicker.classList.add(PICKER_HIDDEN);
            this.components.datePicker.classList.remove(PICKER_HIDDEN);

            // Update button icon
            this.components.datePickerInputButtonIcon.classList.remove(FA_CALENDAR);
            this.components.datePickerInputButtonIcon.classList.add(FA_CLOCK);

            // Update mode
            this._dateTimePickerMode = "date";
        }
    }

    /** Set input text value dynamically with datePicker value and stop setSelectedDatePicker event propagation*/
    private _onValueDatePickerSet(event: Event): void {
        if (this.disabled) { return; }// Don't allow ValueDatePickerSet callback if component is disabled

        const newDateValue = this.components.datePicker.getValue();
        if (newDateValue != null) {
            const newDate = toDate(newDateValue);
            this._value = set(this._value, { year: newDate.year, ordinal: newDate.ordinal }).uts;
        }

        this._dispatchOnPickerValueChange();
        this._updateInputTextValue();
        event.stopPropagation();
    }

    /** Set input text value dynamically with timePicker value and stop setTimePicker event propagation*/
    private _onValueTimePickerSet(event: Event): void {
        if (this.disabled) { return; }// Don't allow ValueTimePickerSet callback if component is disabled

        const minutes = this.components.timePicker.getValue();
        if (minutes != null) {
            this._value = set(this._value, { minuteOfDay: minutes }).uts;
        }

        this._dispatchOnPickerValueChange();
        this._updateInputTextValue();
        event.stopPropagation();
    }

    /**
     * Update the value of the date and time input text from [[selectedDateTime]] 
     */
    private _updateInputTextValue(): void {
        this.components.datePickerInput.value = this.formatDisplayValue(this._value);
    }

    /**
     * Update date and time value from input change
     */
    private _updateDateAndTimeFromInput(): void {
        const inputFormat = this.timePickerOptions.hourAmPm ? DEFAULT_DATE_FORMAT + " hh:MM a" : DEFAULT_DATETIME_FORMAT;
        this.setValue(parse(this.components.datePickerInput.value, inputFormat).uts);

        this._dispatchOnPickerValueChange();
    }

    /**
     * Format the current string passed to be well use as value setter
     */
    public formatDisplayValue(displayedValue: UnixTimestamp): string {
        if (this.timePickerOptions.hourAmPm) {
            return format(displayedValue, DEFAULT_DATE_FORMAT + " hh:MM a") ?? "";
        }
        return format(displayedValue, DEFAULT_DATETIME_FORMAT) ?? "";
    }


    /** @inheritdoc */
    public override refreshDisabled(): void {
        super.refreshDisabled();
        this.datePickerOptions.setAndRefreshIfChanged({ disabled: this.disabled });
        this.timePickerOptions.setAndRefreshIfChanged({ disabled: this.disabled });
    }
}